import React from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SearchIcon from '@mui/icons-material/Search';

const brandColor = '#0B60B1';
const brandGradient = 'linear-gradient(135deg, #0B60B1 0%, #1e90ff 100%)';

const services = [
  {
    icon: <EmailIcon sx={{ fontSize: 40 }} />,
    title: 'Email Services',
    description: 'Comprehensive email deliverability solutions including SPF, DKIM, and DMARC setup, email warmup, and reputation management.',
    color: brandColor,
    gradient: brandGradient,
  },
  {
    icon: <LinkedInIcon sx={{ fontSize: 40 }} />,
    title: 'LinkedIn Services',
    description: 'Professional LinkedIn profile optimization, connection building, and engagement strategies to maximize your network impact.',
    color: brandColor,
    gradient: brandGradient,
  },
  {
    icon: <SearchIcon sx={{ fontSize: 40 }} />,
    title: 'SEO Services',
    description: 'Strategic search engine optimization to improve your online visibility, drive organic traffic, and enhance your digital presence.',
    color: brandColor,
    gradient: brandGradient,
  },
];

const ServicesOverview: React.FC = () => {
  return (
    <Box
      sx={{
        py: 8,
        px: { xs: 2, md: 4 },
        background: `radial-gradient(circle at top right, ${brandColor}15, transparent 70%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 800,
            mb: 6,
            background: brandGradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          Our Core Services
        </Typography>

        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: 4,
          maxWidth: 1200,
          mx: 'auto',
        }}>
          {services.map((service, index) => (
            <Box key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(11,96,177,0.1)',
                      background: 'rgba(255, 255, 255, 0.95)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      background: brandGradient,
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(11,96,177,0.2)',
                    }}
                  >
                    {service.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: brandColor,
                      mb: 2,
                    }}
                  >
                    {service.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3, flexGrow: 1 }}
                  >
                    {service.description}
                  </Typography>
                  <Button
                    component={Link}
                    to="/contact"
                    variant="outlined"
                    sx={{
                      borderColor: brandColor,
                      color: brandColor,
                      '&:hover': {
                        borderColor: brandColor,
                        background: `${brandColor}10`,
                      },
                    }}
                  >
                    Learn More
                  </Button>
                </Paper>
              </motion.div>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ServicesOverview; 